import lodashStartCase from "lodash/startCase";

import RvxTag from "components/RvxTag";
import { importSuffix } from "components/LogItem/messages/importSuffix";
import {
  MODEL_ROUTES,
  getModelLink,
  parseCustomTableAuditableType,
  resolveModelDisplay,
} from "components/LogItem/messages/modelLinks";

export const MANUAL_ACTIVITY_NAME_MAPPINGS = {
  whatsapp: "WhatsApp",
  sms: "SMS",
  linkedin: "LinkedIn",
};

const MANUAL_ACTIVITY_TYPES = [
  "TextMessage",
  "CampaignTargetCall",
  "CampaignTargetEmail",
];

// "SMS" is read letter by letter, so it takes "an" like a vowel.
const withArticle = noun =>
  /^(SMS|[aeiou])/i.test(noun) ? `an ${noun}` : `a ${noun}`;

function manualActivityPhrase(auditableType, auditableId, messageType) {
  if (auditableType === "TextMessage") {
    const typeName = MANUAL_ACTIVITY_NAME_MAPPINGS[messageType] || messageType;
    return `${withArticle(`${typeName || "text"} message`)} (ID: ${auditableId})`;
  }
  if (auditableType === "CampaignTargetCall") {
    return `a call (ID: ${auditableId})`;
  }
  return `an email (ID: ${auditableId})`;
}

export function getGenericMessage({
  user_name,
  contact_id,
  contact_name,
  entity_id,
  entity_name,
  auditable_type,
  value,
  action,
  campaign_id,
  campaign_name,
  data_job_id = null,
  tag = null,
  updated_tag = null,
  field_name = null,
  case_id = null,
  case_name = null,
  revenue_opportunity_id = null,
  auditable_id = null,
  message_type = null,
  custom_table_singular_name = null,
  machine_id = null,
}) {
  const user = user_name || "System";
  const suffix = importSuffix(data_job_id);
  const model = resolveModelDisplay({
    revenue_opportunity_id,
    auditable_type,
    auditable_id,
    custom_table_singular_name,
    machine_id,
    contact_id,
    contact_name,
    case_id,
    case_name,
    entity_id,
    entity_name,
    campaign_name,
  });
  const attachedToModel = model && <> attached to {model}</>;

  if (parseCustomTableAuditableType(auditable_type)) {
    return (
      <p>
        {user} {action} {model}
        {suffix}.
      </p>
    );
  }

  const onTag = tag ? (
    <>
      {" "}
      on the tag <RvxTag tag={updated_tag} />
    </>
  ) : null;

  if (auditable_type === "RevenueOpportunity") {
    return getRevenueOpportunityGenericMessage({
      user,
      action,
      auditable_id,
      entity_id,
      entity_name,
      campaign_id,
      campaign_name,
      case_id,
      case_name,
      suffix,
      onTag,
    });
  }

  if (MANUAL_ACTIVITY_TYPES.includes(auditable_type)) {
    return (
      <p>
        {user} {action}{" "}
        {manualActivityPhrase(auditable_type, auditable_id, message_type)}
        {attachedToModel}
        {suffix}.
      </p>
    );
  }

  let subject;
  if (auditable_type === "TagMetadataValue" && field_name) {
    subject = (
      <>
        the metadata field &apos;{field_name}&apos; with the value &apos;
        {value}&apos;{attachedToModel}
      </>
    );
  } else if (auditable_type === "Machine") {
    // A machine is a top-level record on its case, not a sub-entity dangling off
    // one, so it gets the same phrasing here as on the "changed" path.
    subject = (
      <>
        {model} attached to case{" "}
        {getModelLink(case_id, "Case", { label: case_name })}
      </>
    );
  } else if (auditable_type === "Lead") {
    subject = "a CRM lead";
  } else if (auditable_type && !(auditable_type in MODEL_ROUTES)) {
    subject = (
      <>
        {lodashStartCase(auditable_type).toLowerCase()}
        {value ? ` '${value}'` : ""}
        {attachedToModel}
      </>
    );
  } else {
    subject = model;
  }

  if (auditable_type === "CampaignCase" && action === "deleted") {
    subject = (
      <>
        {subject} from{" "}
        {getModelLink(campaign_id, "Campaign", { label: campaign_name })}
      </>
    );
  }

  return (
    <div>
      {user} {action} {subject}
      {onTag}
      {suffix}.
    </div>
  );
}

export function getRevenueOpportunityGenericMessage({
  user,
  action,
  auditable_id,
  entity_id,
  entity_name,
  campaign_id,
  campaign_name,
  case_id,
  case_name,
  suffix,
  onTag,
}) {
  return (
    <div>
      {user} {action} the revenue opportunity{" "}
      {getModelLink(auditable_id, "RevenueOpportunity", {
        label: `#${auditable_id}`,
      })}{" "}
      for the entity {getModelLink(entity_id, "Entity", { label: entity_name })}
      {campaign_id ? (
        <>
          {" "}
          in the campaign{" "}
          {getModelLink(campaign_id, "Campaign", { label: campaign_name })}
        </>
      ) : null}
      {case_id ? (
        <> on the case {getModelLink(case_id, "Case", { label: case_name })}</>
      ) : null}
      {onTag}
      {suffix}.
    </div>
  );
}
